import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactSelect from "react-select";
import AICCValueSource from "./AICCValueSource";
import AICCFieldSelector from "./AICCFieldSelector";
import { AICCBuilderInitialConfig } from "constants/AICCBuilderConfig";

export function AICCArithmeticSelector({
  arithmeticOperators = AICCBuilderInitialConfig.arithmeticOperators,
  selectedOp,
  columnsWithGroup,
  allowValueSource = 1,
  selectValueSource,
  useColumnGroup,
  columnList,
  leftArOpFieldGroup,
  value,
  onOpChange,
  onChange,
  enabled,
  onCheckChanged,
  onChangeMultipleField,
}) {
  const [t] = useTranslation();
  const arithmeticOpRef = useRef();
  const leftArOpFieldSelectRef = useRef();
  useEffect(() => {
    if (!selectedOp) {
      if (arithmeticOpRef?.current) arithmeticOpRef?.current.clearValue();
    }
  }, [selectedOp]);

  const finalColumnList = useMemo(
    () =>
      useColumnGroup
        ? (columnsWithGroup.find(
            (e) => "" + e?.value === "" + leftArOpFieldGroup
          )?.subFields ?? [])
        : columnList,
    [useColumnGroup, columnsWithGroup, columnList, leftArOpFieldGroup]
  );

  return (
    <div
      className={`row g-0 gap-2 align-items-center h-100 aicc-ruleBuilder__rule--arithmetic-operators ${
        enabled ? "arith-active" : ""
      }`}
    >
      <div className="col-auto">
        <OverlayTrigger
          placement="auto"
          delay={{ show: 200 }}
          overlay={(props) => (
            <Tooltip id="button-tooltip" {...props}>
              {t("aiccRuleBuilder.arithmeticCheckboxTooltip")}
            </Tooltip>
          )}
        >
          <Form.Check
            checked={enabled}
            onChange={(e) => {
              if (onCheckChanged) {
                onCheckChanged(e.target.checked);
              }
            }}
          />
        </OverlayTrigger>
      </div>

      {enabled && (
        <div className="col-auto">
          <ReactSelect
            ref={arithmeticOpRef}
            options={arithmeticOperators}
            classNamePrefix={"select"}
            menuPosition={"fixed"}
            placeholder={t("aiccRuleBuilder.arithmeticSelectPlaceholder")}
            value={arithmeticOperators.find((e) => e.value === selectedOp)}
            onChange={(option) => {
              if (onOpChange) {
                onOpChange("" + (option?.value ?? ""));
              }
            }}
          />
        </div>
      )}
      {enabled && selectedOp && allowValueSource && (
        <div className="col-auto aicc-ruleBuilder__rule--value-source">
          <AICCValueSource
            value={selectValueSource}
            onChange={(valueSource) => {
              if (onChangeMultipleField) {
                onChangeMultipleField({
                  leftArOpValueSource: valueSource,
                  leftArOpValue: ``,
                  leftArOpFieldGroup: ``,
                });
              }
            }}
          />
        </div>
      )}
      {selectValueSource === "field"
        ? enabled &&
          selectedOp && (
            <Form.Group className="form-group mb-0 col-auto row g-0 gap-2">
              <div className="col-auto aicc-ruleBuilder__rule--fields-group">
                {useColumnGroup && (
                  <AICCFieldSelector
                    columnList={columnsWithGroup}
                    value={leftArOpFieldGroup}
                    placeholder={t("aiccRuleBuilder.fieldStreamPlaceholder")}
                    noOptionsLabel={t("aiccRuleBuilder.noFieldStreamOptions")}
                    onChange={(e) => {
                      if (onChangeMultipleField) {
                        onChangeMultipleField({
                          leftArOpFieldGroup: e.value,
                          leftArOpValue: ``,
                        });
                        setTimeout(() => {
                          if (leftArOpFieldSelectRef?.current) {
                            leftArOpFieldSelectRef.current.clearSelect();
                          }
                        }, 10);
                      }
                    }}
                  />
                )}
              </div>
              <div className="col-auto aicc-ruleBuilder__rule--fields-group">
                <AICCFieldSelector
                  columnList={finalColumnList}
                  value={value}
                  ref={leftArOpFieldSelectRef}
                  placeholder={t("aiccRuleBuilder.fieldPlaceholder")}
                  noOptionsLabel={t("aiccRuleBuilder.noFieldOptions")}
                  onChange={(e) => {
                    if (onChange && selectedOp) {
                      onChange(e?.value);
                    }
                  }}
                />
              </div>
            </Form.Group>
          )
        : enabled &&
          selectedOp && (
            <Form.Group className="form-group mb-0 col-auto">
              <Form.Control
                type="number"
                placeholder={t("aiccRuleBuilder.arithmeticValuePlaceholder")}
                value={value}
                onChange={(e) => {
                  if (onChange && selectedOp) {
                    onChange(e.target.value);
                  }
                }}
                onWheel={(event) => {
                  event.target.blur();
                }}
              />
            </Form.Group>
          )}
    </div>
  );
}
